import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import RichText from './RichText';
import { buildApiUrl } from '../lib/api';
import {
  findPostByRouteId,
  formatPostDate,
  getReadTimeLabel,
  normalizePost,
  normalizePosts,
  normalizeRoutePostId,
} from '../lib/posts';

async function fetchJson(url) {
  const response = await fetch(url);

  if (!response.ok) {
    const error = new Error('Failed to fetch post');
    error.status = response.status;
    throw error;
  }

  return response.json();
}

function unwrapPostPayload(payload) {
  if (!payload) {
    return null;
  }

  if (Array.isArray(payload)) {
    return payload[0] ?? null;
  }

  if (payload.data && !Array.isArray(payload.data)) {
    return payload.data;
  }

  if (payload.post) {
    return payload.post;
  }

  return payload;
}

async function fetchPost(routeId) {
  const normalizedRouteId = normalizeRoutePostId(routeId);
  const candidateIds = [...new Set([routeId, normalizedRouteId].filter(Boolean))];
  let lastError;

  for (const candidateId of candidateIds) {
    try {
      const payload = await fetchJson(buildApiUrl(`/blog/${encodeURIComponent(candidateId)}`));
      const post = normalizePost(unwrapPostPayload(payload));

      if (post) {
        return post;
      }
    } catch (error) {
      lastError = error;

      if (error.status !== 404) {
        throw error;
      }
    }
  }

  const params = new URLSearchParams({ page: '1', limit: '100' }).toString();
  const listPayload = await fetchJson(buildApiUrl(`/blog?${params}`));
  const posts = normalizePosts(Array.isArray(listPayload) ? listPayload : listPayload.data || []);
  const matchingPost = findPostByRouteId(posts, routeId);

  if (matchingPost) {
    return matchingPost;
  }

  throw lastError ?? new Error('Post not found');
}

export default function BlogPost() {
  const { id = '' } = useParams();

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blogPost', id],
    queryFn: () => fetchPost(id),
    enabled: Boolean(id),
  });

  if (isLoading) {
    return (
      <main className="page-shell">
        <div className="page-narrow surface-card flex min-h-[280px] items-center justify-center text-slate-600">
          Loading post...
        </div>
      </main>
    );
  }

  if (error || !post) {
    return (
      <main className="page-shell">
        <div className="page-narrow">
          <Link to="/blog" className="text-link">
            Back to blog
          </Link>
          <div className="mt-10 rounded-[28px] border border-rose-200 bg-rose-50 p-6 text-rose-700">
            {error?.message || 'This post could not be loaded.'}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="page-shell">
      <article className="page-narrow">
        <Link to="/blog" className="text-link">
          Back to blog
        </Link>

        <header className="surface-card mt-10 px-8 py-10 md:px-10 md:py-12">
          <div className="mb-5 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500">
            <span>{formatPostDate(post.createdAt)}</span>
            <span>/</span>
            <span>{getReadTimeLabel(post.content)}</span>
          </div>

          <h1 className="text-4xl font-medium leading-tight tracking-tight text-slate-900 md:text-5xl">
            {post.title}
          </h1>

          {post.excerpt && <p className="mt-6 text-base leading-8 text-slate-600 md:text-lg">{post.excerpt}</p>}
        </header>

        <section className="surface-card mt-8 px-8 py-10 md:px-10 md:py-12">
          {post.content ? <RichText content={post.content} className="max-w-none" /> : <p className="text-slate-500">Content not available.</p>}
        </section>
      </article>
    </main>
  );
}
